import { useMemo } from "react";
import { useDashboard, useTasks } from "../hooks/useTasks";
import { useUsers } from "../hooks/useDirectory";
import { useAssignmentsIndex } from "../hooks/useAssignmentsIndex";
import { useUiStore } from "../state/uiStore";
import { isOpenStatus } from "../lib/taskVM";
import { isoToDayKey, isOverdue, formatDay } from "../lib/date";
import { Avatar } from "../components/Avatar";
import { displayName } from "../api/tawa";
import type { TmTask } from "../api/tm";

interface MemberStats {
  open: number;
  overdue: number;
  done: number;
  nextDueKey?: string;
  activeTasks: TmTask[];
}

/** Vue équipe manager — KPI globaux (dashboard TM) + carte par membre avec
 * barre de charge, prochaine échéance et tâches actives (DT F-03). */
export function TeamScreen() {
  const users = useUsers();
  const tasks = useTasks();
  const dashboard = useDashboard();
  const { selectedTaskId, selectTask } = useUiStore();
  const taskIds = useMemo(
    () => (tasks.data ?? []).map((t) => t.id),
    [tasks.data],
  );
  const assignments = useAssignmentsIndex(taskIds);

  const rows = useMemo(() => {
    const perUser = new Map<string, MemberStats>();
    const byId = new Map((tasks.data ?? []).map((t) => [t.id, t]));
    for (const [taskId, asg] of Object.entries(assignments.data ?? {})) {
      const t = byId.get(taskId);
      if (!t) continue;
      for (const a of asg) {
        if (!a.active || a.actor_type !== "user") continue;
        const cur = perUser.get(a.actor_ref) ?? {
          open: 0,
          overdue: 0,
          done: 0,
          activeTasks: [],
        };
        if (isOpenStatus(t.status)) {
          cur.open++;
          cur.activeTasks.push(t);
          if (t.due_date) {
            const k = isoToDayKey(t.due_date);
            if (isOverdue(k)) cur.overdue++;
            if (!cur.nextDueKey || k < cur.nextDueKey) cur.nextDueKey = k;
          }
        } else if (t.status === "done") {
          cur.done++;
        }
        perUser.set(a.actor_ref, cur);
      }
    }
    for (const s of perUser.values())
      s.activeTasks.sort((a, b) =>
        (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999"),
      );
    return perUser;
  }, [assignments.data, tasks.data]);

  const members = (users.data ?? []).filter((u) => u.enabled);
  const d = dashboard.data;
  const kpiOpen = d
    ? (d.by_status["todo"] ?? 0) +
      (d.by_status["in_progress"] ?? 0) +
      (d.by_status["blocked"] ?? 0)
    : 0;

  return (
    <>
      <div className="view-header">
        <div className="view-title">Équipe</div>
        <div className="view-caption">
          Membres · charge ouverte, retards, terminées
        </div>
      </div>
      <div className="main-scroll">
        {d && (
          <div className="kpi-cards">
            <div className="kpi-card">
              <div className="kpi-value">{d.total}</div>
              <div className="kpi-label">Tâches totales</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-value">{kpiOpen}</div>
              <div className="kpi-label">Ouvertes</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-value" style={{ color: "var(--danger)" }}>
                {d.overdue}
              </div>
              <div className="kpi-label">En retard</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-value" style={{ color: "var(--success)" }}>
                {d.by_status["done"] ?? 0}
              </div>
              <div className="kpi-label">Terminées</div>
            </div>
          </div>
        )}

        {(users.isLoading || tasks.isLoading) && (
          <div className="empty-state">Chargement…</div>
        )}
        {users.isError && (
          <div className="empty-state">
            Annuaire indisponible (TAWA requis).
          </div>
        )}

        <div className="member-grid">
          {members.map((u) => {
            const stats =
              rows.get(u.worker_url) ??
              rows.get(u.username) ??
              rows.get(u.id) ??
              rows.get(u.email);
            const open = stats?.open ?? 0;
            // ponytail: charge = min(100, open×22) comme le prototype — pas de
            // capacité réelle par membre côté backend.
            const load = Math.min(100, open * 22);
            return (
              <div key={u.id} className="member-card">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar name={displayName(u)} size={28} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 12.5 }}>
                      {displayName(u)}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 10.5,
                        color: "var(--ink-faint)",
                      }}
                    >
                      {u.email}
                    </span>
                  </span>
                  <span className="tag" style={{ marginLeft: "auto" }}>
                    {u.tawa_role ?? "worker"}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <span
                    className="progress-bar"
                    style={{ flex: 1 }}
                    title={`Charge : ${open} ouvertes`}
                  >
                    <span
                      className="progress-fill"
                      style={{
                        width: `${load}%`,
                        background:
                          load >= 88 ? "var(--danger)" : "var(--accent)",
                      }}
                    />
                  </span>
                  <span style={{ fontSize: 10.5, color: "var(--ink-faint)" }}>
                    {stats?.nextDueKey
                      ? `◷ ${formatDay(stats.nextDueKey)}`
                      : "—"}
                  </span>
                </div>
                <div className="member-counters">
                  <span>{open} ouvertes</span>
                  <span
                    style={
                      stats?.overdue
                        ? { color: "var(--danger)", fontWeight: 600 }
                        : undefined
                    }
                  >
                    {stats?.overdue ?? 0} en retard
                  </span>
                  <span>{stats?.done ?? 0} terminées</span>
                </div>
                {(stats?.activeTasks ?? []).slice(0, 4).map((t) => (
                  <button
                    key={t.id}
                    className={`member-task${t.id === selectedTaskId ? " selected" : ""}`}
                    onClick={() =>
                      selectTask(t.id === selectedTaskId ? null : t.id)
                    }
                  >
                    <span className="tl-title">{t.title}</span>
                    {t.due_date && (
                      <span
                        className="task-due"
                        style={
                          isOverdue(isoToDayKey(t.due_date))
                            ? { color: "var(--danger)", fontWeight: 600 }
                            : undefined
                        }
                      >
                        {formatDay(isoToDayKey(t.due_date))}
                      </span>
                    )}
                  </button>
                ))}
                {(stats?.activeTasks.length ?? 0) > 4 && (
                  <div style={{ fontSize: 10.5, color: "var(--ink-ghost)" }}>
                    + {stats!.activeTasks.length - 4} autres
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {members.length === 0 && !users.isLoading && !users.isError && (
          <div className="empty-state">Aucun membre actif.</div>
        )}
        {rows.size > 0 && (
          <p style={{ fontSize: 11, color: "var(--ink-ghost)", marginTop: 14 }}>
            Compteurs calculés sur les tâches visibles par vous.
          </p>
        )}
      </div>
    </>
  );
}
